/*
  Component: Product gallery video — offscreen pause
  Responsibility: stop a product video that has scrolled out of the gallery, and pick it up
  again when it comes back. Nothing here makes a video loop: that is the `loop` attribute
  Dawn's media_tag writes from the section's "Enable video looping" setting.

  The video element is injected by Dawn's <deferred-media> when the poster is clicked, and
  it is not ours to put an attribute on. Rather than reach in and look for it by tag name,
  this listens for `play` in the capture phase — media events do not bubble, but they can be
  captured — so a video announces itself the moment it starts, whenever that happens to be.

  A visitor who pauses the video themselves has said what they want, so it is left paused
  when it scrolls back into view. Only a pause this file caused is undone by this file.
*/

const KL_PRODUCT_VIDEO_HOST_SELECTOR = '[data-kl-product-video]';
const KL_PRODUCT_VIDEO_VISIBLE_RATIO = 0.25;

const watchedVideos = new WeakMap();

function requestPlayback(video) {
  const playRequest = video.play();

  if (!playRequest || typeof playRequest.catch !== 'function') return;

  playRequest.catch((error) => {
    if (error.name === 'AbortError') return;

    console.debug('kl-product-video: the browser refused to resume this video', {
      source: video.currentSrc,
      error,
    });
  });
}

function watchVideo(video) {
  if (watchedVideos.has(video)) return;

  if (typeof IntersectionObserver !== 'function') return;

  const state = { isPausedByScroll: false, isPauseFromHere: false };

  const observer = new IntersectionObserver(
    (entries) => {
      if (!video.isConnected) {
        observer.disconnect();
        watchedVideos.delete(video);
        return;
      }

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!state.isPausedByScroll) return;

          state.isPausedByScroll = false;
          requestPlayback(video);
          return;
        }

        if (video.paused) return;

        state.isPausedByScroll = true;
        state.isPauseFromHere = true;
        video.pause();
      });
    },
    { threshold: KL_PRODUCT_VIDEO_VISIBLE_RATIO }
  );

  watchedVideos.set(video, state);
  observer.observe(video);
}

document.addEventListener(
  'play',
  (event) => {
    const video = event.target;

    if (typeof video.closest !== 'function') return;
    if (!video.closest(KL_PRODUCT_VIDEO_HOST_SELECTOR)) return;

    watchVideo(video);
  },
  true
);

document.addEventListener(
  'pause',
  (event) => {
    const state = watchedVideos.get(event.target);

    if (!state) return;

    if (state.isPauseFromHere) {
      state.isPauseFromHere = false;
      return;
    }

    // The visitor's own pause. Leave it alone from here on.
    state.isPausedByScroll = false;
  },
  true
);
